import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { Minister } from '@/store/minister/minister.model';
import { MinisterSchema } from '@/store/minister/minister.validator';
import { MinisterAction, MinisterGetter } from '@/store/minister/minister.module';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'MinisterFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    ministerId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.ministerId != null);
    const title = computed(() => isEditMode.value ? 'แก้ไข ร.นรม./รมต.นร.' : 'เพิ่ม ร.นรม./รมต.นร.');
    const minister = computed<Minister | undefined>(() => store.getters[MinisterGetter.BY_ID](props.ministerId));
    const ministerForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref();
    const photoPreview = computed<string>(() => {
      return formData.photo
        ? fileDialog.value?.files?.length !== 0
          ? URL.createObjectURL(fileDialog.value?.files?.item(0) as File)
          : formData.photo
        : require('@/assets/images/user.png');
    });

    // form
    interface Form {
      id?: number | null;
      prefix?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      photo?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      prefix: '',
      firstName: '',
      lastName: '',
      photo: ''
    });
    const validator = computed(() => MinisterSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      prefix: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'prefix')),
      firstName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'firstName')),
      lastName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'lastName')),
      photo: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'photo'))
    });
    const isValid = computed(() => !errors.prefix && !errors.firstName && !errors.lastName && !errors.photo);
    const isLoading = computed<boolean>(() => store.getters[MinisterGetter.STATUS] === 'init' || store.getters[MinisterGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.ministerId;
          formData.prefix = minister.value?.prefix;
          formData.firstName = minister.value?.firstName;
          formData.lastName = minister.value?.lastName;
          formData.photo = minister.value?.photo;
        }
        resolve();
      })
      .then(() => ministerForm.value?.show());
    };

    // hide modal
    const hide = () => {
      ministerForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.prefix = '';
        formData.firstName = '';
        formData.lastName = '';
        formData.photo = '';
        fileDialog.value.value = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(MinisterAction.UPDATE, {
            id: formData.id,
            prefix: formData.prefix,
            firstName: formData.firstName,
            lastName: formData.lastName,
            photo: (fileDialog.value?.files?.length !== 0) ? fileDialog.value?.files?.item(0) as File : null,
          }).then(() => hide());
        } else {
          store.dispatch(MinisterAction.CREATE, {
            prefix: formData.prefix,
            firstName: formData.firstName,
            lastName: formData.lastName,
            photo: fileDialog.value?.files?.item(0) as File,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(MinisterAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    const fileDialogClick = () => {
      fileDialog.value?.click();
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.photo = file.name;
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      ministerForm,
      fileDialog,
      photoPreview,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm,
      fileDialogClick,
      fileDialogChange,
    };
  }
});
